import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import MotionWrapper from '@components/wrapper/MotionWrapper';
import NPCSec from '../NPC/NPCSec';
import LuckyDrawSec from '../LuckyDraw/LuckyDrawSec';
import StoreItemList from '../Detail/StoreItemList';
import BackBtn from '@components/dataEntry/button/BackBtn';
import { useNavigate } from 'react-router-dom';
import { scrollbarStyle, scrollbarHide } from '@styles/variants/scrollbar';
import { IstoreCommon } from '@typings/store/storeCommon.interface';
import { Ihashtag } from '@typings/common/hashtag.interface';
import StoreControlSec from '../component/StoreControlSec';
import showNav from '@utils/showNav';

type Props = {
  setData: IstoreCommon | null | undefined;
  visibleHashtagList: Ihashtag[];
  isMobile: boolean;
  windowWidth: number;
};

function FullPageLayout({
  setData,
  visibleHashtagList,
  isMobile,
  windowWidth,
}: Props) {
  showNav(false);
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<
    'luckydraw' | 'all' | string
  >('all');

  const handleGoBack = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleSelectCategory = useCallback((category: string) => {
    setSelectedCategory(category);
  }, []);

  return (
    <MotionWrapper>
      <FullPageWrapper $isMobile={isMobile}>
        <GameContainer>
          <TopBar>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <BackBtn onClick={handleGoBack} />
              <LogoSection>
                {setData?.logoImgURL ? (
                  <img src={setData.logoImgURL} alt="상점 로고" />
                ) : (
                  <h5>상점</h5>
                )}
              </LogoSection>
            </div>

            <StoreControlSec />
          </TopBar>

          {isMobile && (
            <MobileHashtagBar>
              <MobileHashtagButton
                $active={selectedCategory === 'luckydraw'}
                onClick={() => handleSelectCategory('luckydraw')}
              >
                뽑기
              </MobileHashtagButton>
              <MobileHashtagButton
                $active={selectedCategory === 'all'}
                onClick={() => handleSelectCategory('all')}
              >
                전체
              </MobileHashtagButton>
              {visibleHashtagList?.map((tag) => (
                <MobileHashtagButton
                  key={tag.id}
                  $active={selectedCategory === tag.id}
                  onClick={() => handleSelectCategory(tag.id || '')}
                >
                  {tag.value}
                </MobileHashtagButton>
              ))}
            </MobileHashtagBar>
          )}

          <MainContent>
            {!isMobile && (
              <SideMenu>
                <MenuSection>
                  <MenuItem
                    active={selectedCategory === 'luckydraw'}
                    onClick={() => handleSelectCategory('luckydraw')}
                  >
                    뽑기 패키지
                  </MenuItem>
                  <MenuItem
                    active={selectedCategory === 'all'}
                    onClick={() => handleSelectCategory('all')}
                  >
                    전체 아이템
                  </MenuItem>
                  {visibleHashtagList?.map((tag) => (
                    <MenuItem
                      key={tag.id}
                      active={selectedCategory === tag.id}
                      onClick={() => handleSelectCategory(tag.id || '')}
                    >
                      {tag.value}
                    </MenuItem>
                  ))}
                </MenuSection>
              </SideMenu>
            )}

            <ContentArea>
              {selectedCategory === 'luckydraw' ? (
                <LuckyDrawSec />
              ) : (
                <StoreItemList
                  selectedHashtag={selectedCategory}
                  showHashtag={false}
                  showWallet={false}
                />
              )}
            </ContentArea>

            {windowWidth > 1024 && (
              <SideMenu>
                <NPCSec />
              </SideMenu>
            )}
          </MainContent>
        </GameContainer>
      </FullPageWrapper>
    </MotionWrapper>
  );
}

export default FullPageLayout;

const FullPageWrapper = styled.div<{ $isMobile: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100dvh;
  padding: ${({ $isMobile }) => ($isMobile ? '0' : '20px')};

  @media (max-width: 480px) {
    padding: 0;
    padding-top: env(safe-area-inset-top, 0px);
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }
`;

const GameContainer = styled.div`
  width: 100%;
  height: calc(
    100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)
  );
  max-width: 1600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;
  position: relative;

  @media (max-width: 480px) {
    padding-top: env(safe-area-inset-top, 0px);
    padding: env(safe-area-inset-top, 0px) 0 0 0;
    border-radius: 0;
    max-width: 100%;
  }
`;

const TopBar = styled.div`
  width: 100%;
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 2;
  flex-shrink: 0;

  @media (max-width: 480px) {
    padding: 12px 16px;
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  img {
    max-height: 50px;
    object-fit: contain;
  }

  h5 {
    font-size: 20px;
    font-weight: 700;
    color: var(--textColor);
    margin: 0;
  }
`;

const MobileHashtagBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px 12px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 1;
  ${scrollbarHide}
`;

const MobileHashtagButton = styled.button<{ $active?: boolean }>`
  flex-shrink: 0;
  padding: 6px 16px;
  border: 1.5px solid
    ${({ $active }) => ($active ? 'var(--primaryColor)' : 'var(--lineColor)')};
  background: ${({ $active }) =>
    $active ? 'var(--primaryColor)' : 'transparent'};
  color: ${({ $active }) => ($active ? 'white' : 'var(--textColor)')};
  font-size: 13px;
  font-weight: 500;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: ${({ $active }) =>
      $active ? 'var(--primaryColor)' : 'var(--inkColor)'};
  }
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 220px 1fr 420px;
  gap: 20px;
  padding: 20px;
  flex: 1;
  overflow: hidden;

  @media (max-width: 1024px) {
    grid-template-columns: 200px 1fr;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 480px) {
    padding: 0 16px 16px;
    gap: 12px;
  }
`;

const SideMenu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  overflow-y: auto;
  ${scrollbarStyle}

  @media (max-width: 768px) {
    height: auto;
  }
`;

const MenuSection = styled.div`
  background: var(--cardBackgroundColor);
  backdrop-filter: blur(8px);
  border: 1px solid color-mix(in srgb, var(--cardLineColor) 60%, transparent);
  border-radius: var(--cardBorderRadius);
  overflow: hidden;
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MenuItem = styled.button<{ active?: boolean }>`
  width: 100%;
  padding: 12px 14px;
  border: none;
  border-radius: 6px;
  background: ${({ active }) =>
    active
      ? 'color-mix(in srgb, var(--primaryColor) 18%, transparent)'
      : 'transparent'};
  color: ${({ active }) =>
    active ? 'var(--primaryColor)' : 'var(--textColor)'};
  text-align: left;
  font-size: 14px;
  font-weight: ${({ active }) => (active ? '600' : '400')};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  letter-spacing: 0.02em;

  &::before {
    content: '';
    width: 3px;
    height: ${({ active }) => (active ? '60%' : '0%')};
    background: var(--primaryColor);
    border-radius: 2px;
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    transition: height 0.25s ease;
  }

  &:hover {
    background: ${({ active }) =>
      active
        ? 'color-mix(in srgb, var(--primaryColor) 22%, transparent)'
        : 'color-mix(in srgb, var(--inkColor) 60%, transparent)'};
    color: ${({ active }) =>
      active ? 'var(--primaryColor)' : 'var(--textColor)'};

    &::before {
      height: ${({ active }) => (active ? '60%' : '30%')};
      background: ${({ active }) =>
        active ? 'var(--primaryColor)' : 'var(--subTextColor)'};
    }
  }
`;

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
  overflow-y: auto;
  ${scrollbarStyle}
`;
